"use client";

import { ChartOptions } from "chart.js";
import { Bar } from "react-chartjs-2";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { chartColors, chartJsGridColor, chartJsTextColor } from "@portal/helpers/chartJs";

interface Props {
  managmentFees: number[];
  depotFees: number[];
  portfolioValues: number[];
  dates: string[];
  isDefaultLanguage: boolean;
}

/**
 * Formats a number as a compact "k" value, e.g. 100000 -> "100k".
 * @param value
 * @returns the formatted string.
 */
const formatK = (value: number): string => (Math.abs(value) >= 1000 ? `${value / 1000}k` : `${value}`);

/**
 * Chart.js version of the expected value stacked column chart.
 * @param interface
 * @returns a chart.
 */
export const ExpectedValueChart = ({ managmentFees, depotFees, portfolioValues, dates, isDefaultLanguage }: Props) => {
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { stacked: true, ticks: { color: chartJsTextColor() }, grid: { display: false } },
      y: {
        stacked: true,
        ticks: {
          color: chartJsTextColor(),
          padding: 8,
          callback: (value, index) => (index % 2 === 0 ? formatK(value as number) : ""),
        },
        border: { display: false },
        grid: { color: ctx => (ctx.index % 2 === 0 ? chartJsGridColor() : "transparent") },
      },
    },
    plugins: {
      legend: { position: "bottom", labels: { color: chartJsTextColor(), boxWidth: 12 } },
      tooltip: {
        callbacks: {
          label: item => `${item.dataset.label}: ${(item.raw as number).toFixed(2)}`,
        },
      },
    },
  };

  return (
    <div style={{ height: 320 }}>
      <Bar
        data={{
          labels: dates,
          datasets: [
            { label: texts.PAGE_15_VALUE_TOTAL, data: portfolioValues.slice(0, 3), backgroundColor: chartColors.green },
            { label: texts.PAGE_15_COMMISION, data: managmentFees.slice(0, 3), backgroundColor: chartColors.yellow },
            { label: texts.PAGE_15_DEPOT, data: depotFees.slice(0, 3), backgroundColor: chartColors.blueHighcharts },
          ],
        }}
        options={options}
      />
    </div>
  );
};
